//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import equal from 'fast-deep-equal';
import { createSelector } from 'reselect';

import { isLike2BuyV2TikTokDefaultConfig } from '@apis/like2buy-v2/like2BuyV2StoreModel';
import { ICuralateStore } from '@models/AppState';
import { BANNER_CONSTANTS } from '@tiktok/components/like2buy/customizations/storeCustomizationsConstants';
import {
  tiktokCustomizationsToStoreConfig,
  isCtaValid,
  tiktokStoreConfigToCustomizations,
} from '@tiktok/redux/like2buy/customizations/tiktokLike2BuyCustomizationsUtils';
import { getTikTokLike2BuyStoreConfig } from '@tiktok/redux/like2buy/selectors/getTikTokLike2Buy';
import { isDefined } from '@utils/filterPredicates';
import isValidUrl from '@utils/urlValidation';

const getTikTokLike2BuyCustomizationsState = (state: ICuralateStore) =>
  state.tiktok.like2buy.customizations;

export const getTikTokLike2BuyCustomizations = createSelector(
  getTikTokLike2BuyCustomizationsState,
  (customizationsState) => customizationsState.customizations,
);

export const getIsTikTokLike2BuyCustomizationsModalOpen = createSelector(
  getTikTokLike2BuyCustomizationsState,
  (customizationsState) => customizationsState.isStoreCustomizationsModalOpen,
);

export const getIsTikTokLike2BuyPreviewModalOpen = createSelector(
  getTikTokLike2BuyCustomizationsState,
  (customizationsState) => customizationsState.isStorePreviewModalOpen,
);

export const getTikTokLike2BuyPreviewConfigFromCustomizations = createSelector(
  getTikTokLike2BuyStoreConfig,
  getTikTokLike2BuyCustomizations,
  (existingStoreConfig, currentCustomizations) => {
    if (
      !isDefined(existingStoreConfig) ||
      !isLike2BuyV2TikTokDefaultConfig(existingStoreConfig)
    ) {
      return;
    }

    const updatedConfig = tiktokCustomizationsToStoreConfig(
      existingStoreConfig,
      currentCustomizations,
    );

    // Certain customizations need default values for Preview purposes,
    // but they aren't values that we would want to allow to be missing
    // in the actual consumer experience
    return {
      ...updatedConfig,
      ...(!!updatedConfig.galleryBannerSection?.isEnabled && {
        galleryBannerSection: {
          ...updatedConfig.galleryBannerSection,
          crawlId: updatedConfig.galleryBannerSection.crawlId ?? '',
        },
      }),
    };
  },
);

export const getTikTokLike2BuyCustomizationsCtasGalleryHeader = createSelector(
  getTikTokLike2BuyCustomizations,
  (customizations) => customizations.ctas.galleryHeader,
);

export const getTikTokLike2BuyCustomizationsCtasGalleryMediaHover =
  createSelector(
    getTikTokLike2BuyCustomizations,
    (customizations) => customizations.ctas.galleryMediaHover,
  );

export const getTikTokLike2BuyCustomizationsCtasProductsContainer =
  createSelector(
    getTikTokLike2BuyCustomizations,
    (customizations) => customizations.ctas.productsContainer,
  );

export const getTikTokLike2BuyCustomizationsCtasHasError = createSelector(
  getTikTokLike2BuyCustomizationsCtasGalleryHeader,
  getTikTokLike2BuyCustomizationsCtasGalleryMediaHover,
  getTikTokLike2BuyCustomizationsCtasProductsContainer,
  (
    { validationError: galleryHeaderValidationError },
    { validationError: galleryMediaValidationError },
    { validationError: productsContainerValidationError },
  ) =>
    !!galleryHeaderValidationError ||
    !!galleryMediaValidationError ||
    !!productsContainerValidationError,
);

const getTikTokLike2BuyCustomizationsFlags = createSelector(
  getTikTokLike2BuyCustomizationsState,
  (state) => state.flags,
);

export const getTikTokLike2BuyCustomizationsIsUpdatingStoreConfiguration =
  createSelector(
    getTikTokLike2BuyCustomizationsFlags,
    (flags) => flags.isUpdatingStoreConfiguration,
  );

export const getTikTokLike2BuyCustomizationsIsDigestingBannerImage =
  createSelector(
    getTikTokLike2BuyCustomizationsFlags,
    (flags) => flags.isDigestingBannerImage,
  );

export const getTikTokLike2BuyCustomizationsIsProcessingActions =
  createSelector(
    getTikTokLike2BuyCustomizationsIsUpdatingStoreConfiguration,
    getTikTokLike2BuyCustomizationsIsDigestingBannerImage,
    getTikTokLike2BuyCustomizationsFlags,
    (isUpdatingStore, isDigesting, { isCreatingCrawledProduct }) =>
      isUpdatingStore || isDigesting || isCreatingCrawledProduct,
  );

const getTikTokLike2BuyCustomizationsBanner = createSelector(
  getTikTokLike2BuyCustomizations,
  (customizations) => customizations.banner,
);

const getTikTokLike2BuyCustomizationsBannerImageUpload = createSelector(
  getTikTokLike2BuyCustomizationsBanner,
  (banner) => banner.imageUpload,
);

export const getTikTokLike2BuyCustomizationsBannerDestinationUrl =
  createSelector(
    getTikTokLike2BuyCustomizationsBanner,
    ({ destinationUrl }) => destinationUrl,
  );

export const getTikTokLike2BuyCustomizationsBannerAltText = createSelector(
  getTikTokLike2BuyCustomizationsBanner,
  ({ altText }) => altText,
);

export const getTikTokLike2BuyCustomizationsBannerCrawlId = createSelector(
  getTikTokLike2BuyCustomizationsBanner,
  ({ crawlId }) => crawlId,
);

export const getTikTokLike2BuyCustomizationsBannerImageFileId = createSelector(
  getTikTokLike2BuyCustomizationsBannerImageUpload,
  ({ imageFileId }) => imageFileId,
);

export const getTikTokLike2BuyCustomizationsBannerImageFileName =
  createSelector(
    getTikTokLike2BuyCustomizationsBannerImageUpload,
    ({ imageFileName }) => imageFileName,
  );

export const getTikTokLike2BuyCustomizationsBannerImageUploadValidationError =
  createSelector(
    getTikTokLike2BuyCustomizationsBannerImageUpload,
    ({ validationError }) => validationError,
  );

export const getIsTikTokLike2BuyCustomizationsBannerEnabled = createSelector(
  getTikTokLike2BuyCustomizationsBanner,
  ({ isBannerEnabled }) => isBannerEnabled,
);

export const getHasUploadedBannerImage = createSelector(
  getTikTokLike2BuyCustomizationsBannerImageFileId,
  getTikTokLike2BuyCustomizationsBannerImageFileName,
  (fileId, name) => isDefined(fileId) && isDefined(name),
);

export const getIsBannerAltTextValid = createSelector(
  getTikTokLike2BuyCustomizationsBannerAltText,
  ({ text: altText }) =>
    isCtaValid(altText, BANNER_CONSTANTS.bannerAltText.ctaLimit),
);

export const getIsDestinationUrlValid = createSelector(
  getTikTokLike2BuyCustomizationsBannerDestinationUrl,
  ({ text: destinationUrl }) => isValidUrl(destinationUrl),
);

export const getTikTokLike2BuyBannerCustomizationsHasError = createSelector(
  getTikTokLike2BuyCustomizationsBannerDestinationUrl,
  getTikTokLike2BuyCustomizationsBannerImageUploadValidationError,
  getTikTokLike2BuyCustomizationsBannerAltText,
  (
    { validationError: urlValidationError },
    uploadValidationError,
    { validationError: altTextValidationError },
  ) =>
    !!urlValidationError || !!uploadValidationError || !!altTextValidationError,
);

export const getIsTikTokLike2BuyCustomizationsEmailCaptureEnabled =
  createSelector(
    getTikTokLike2BuyCustomizations,
    ({ emailCapture }) => emailCapture.isEnabled,
  );

export const getTikTokLike2BuyCustomizationsEmailCaptureShouldRequireTermsAndConditions =
  createSelector(
    getTikTokLike2BuyCustomizations,
    ({ emailCapture }) => emailCapture.shouldRequireTermsAndConditions,
  );

export const getTikTokLike2BuyCustomizationsEmailCaptureTermsAndConditionsUrl =
  createSelector(
    getTikTokLike2BuyCustomizations,
    ({ emailCapture }) => emailCapture.termsAndConditionsUrl,
  );

export const getIsTermsAndConditionsUrlValid = createSelector(
  getTikTokLike2BuyCustomizationsEmailCaptureTermsAndConditionsUrl,
  ({ text: termsAndConditionsUrl }) => isValidUrl(termsAndConditionsUrl),
);

export const getTikTokLike2BuyCustomizationsEmailCaptureCta = createSelector(
  getTikTokLike2BuyCustomizations,
  ({ emailCapture }) => emailCapture.cta,
);

export const getTikTokLike2BuyCustomizationsEmailCaptureHasError =
  createSelector(
    getTikTokLike2BuyCustomizationsEmailCaptureCta,
    getTikTokLike2BuyCustomizationsEmailCaptureTermsAndConditionsUrl,
    (
      { validationError: ctaValidationError },
      { validationError: urlValidationError },
    ) => !!ctaValidationError || !!urlValidationError,
  );

export const getHasNotUpdatedTikTokLike2BuyCustomizations = createSelector(
  getTikTokLike2BuyCustomizations,
  getTikTokLike2BuyStoreConfig,
  (currentCustomizations, existingConfig) => {
    const existingCustomizations =
      isDefined(existingConfig) &&
      isLike2BuyV2TikTokDefaultConfig(existingConfig)
        ? tiktokStoreConfigToCustomizations(existingConfig)
        : undefined;
    return equal(currentCustomizations, existingCustomizations);
  },
);
